import React, { useState } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { MAX_DECK_COLUMNS } from '@/constants/config';
import Tooltip from '@/components/Tooltip';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';

const COLUMN_OPTIONS = [
  { id: 'conversation', spec: {}, title: 'Chat', subtitle: 'Start a chat or choose from history', icon: 'message', iosIcon: 'bubble.left' },
  { id: 'model', spec: { type: 'model', label: 'Model' }, title: 'Model', subtitle: 'Compare responses from a model', icon: 'cube', iosIcon: 'cube' },
  { id: 'agent', spec: { type: 'agent', label: 'Bot' }, title: 'Bot', subtitle: 'Chat with a specialized bot', icon: 'user-astronaut', iosIcon: 'person.circle' },
  { id: 'tool', spec: { type: 'tool', label: 'Tool' }, title: 'Tool', subtitle: 'Keep a useful tool alongside your chats', icon: 'wand-magic-sparkles', iosIcon: 'wand.and.stars' },
];

export default function DeckAddColumnButton({ onAddColumn = () => {}, columnCount = 0 }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isDisabled = columnCount >= MAX_DECK_COLUMNS;
  const buttonStyle = StyleSheet.flatten([styles.button, {
    backgroundColor: colors.text_color_primary,
    opacity: isDisabled ? 0.4 : hovered ? 0.85 : 1,
    ...(Platform.OS === 'web' && focused ? {
      outlineStyle: 'solid', outlineWidth: 2, outlineOffset: 2, outlineColor: colors.border_color_primary,
    } : {}),
  }]);
  const button = (
    <Pressable
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel="Add column"
      accessibilityHint="Choose a chat, model, bot, or tool"
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={styles.trigger}
    >
      <View style={buttonStyle}>
        <Icon name="plus" size={13} color={colors.solid_color_primary} />
        <Text size="xs" weight="semibold" style={{ color: colors.solid_color_primary }}>Add column</Text>
        <Icon name="chevron-down" size={10} color={colors.solid_color_primary} />
      </View>
    </Pressable>
  );
  if (isDisabled) return <Tooltip label={`Maximum ${MAX_DECK_COLUMNS} columns`} side="bottom">{button}</Tooltip>;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild style={styles.trigger}>{button}</DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent" sideOffset={8} align="end">
        {COLUMN_OPTIONS.map(option => <DropdownMenuItem
          key={option.id} icon={option.icon} iosIcon={option.iosIcon} title={option.title}
          subtitle={option.subtitle} onSelect={() => onAddColumn(option.spec)}
        />)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const styles = StyleSheet.create({
  trigger: { flexShrink: 0, borderRadius: 10 },
  button: {
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});
